import { createApi } from "@reduxjs/toolkit/query/react";

import { FleetsOptionsType } from "@/constants/fleet.constant";
import { IFleet, IFleetOptions } from "@/interfaces/fleet.interface";
import { BaseWithAuth, getWorkspace } from "@/utils/helpers";

export const fleetAPi = createApi({
  reducerPath: "fleetAPi",
  baseQuery: BaseWithAuth(),
  tagTypes: ["fleet", "fleetOptions", "singleFleet"],
  endpoints: (builder) => ({
    getFleet: builder.query<GeneralResponse<IFleet[]>, { filter?: string }>({
      query: ({ filter }) => {
        return `/workspace/${getWorkspace()}/fleets/${filter ?? ""}`;
      },
      providesTags: ["fleet"],
    }),

    getSingleFleet: builder.query<
      GeneralSingleResponse<IFleet>,
      { id: string; filter?: string }
    >({
      query: ({ id, filter }) => {
        return `/workspace/${getWorkspace()}/fleets/${id}${filter ?? ""}`;
      },
      providesTags: ["singleFleet"],
    }),

    getFleetOptions: builder.query<
      IFleetOptions[],
      {
        optionFor?: FleetsOptionsType;
      }
    >({
      query: ({ optionFor }) => {
        return `/workspace/${getWorkspace()}/fleets/options${optionFor ? `?for=${optionFor}` : ""}`;
      },
      providesTags: ["fleetOptions"],
      transformResponse: (response: APIResponse<IFleetOptions[]>) =>
        response?.data,
    }),

    mutateFleet: builder.mutation<
      object,
      { id?: string; data: object; method: "POST" | "PUT" }
    >({
      query: ({ id, data, method }) => ({
        url: `/workspace/${getWorkspace()}/fleets/${id ? id : ""}`,
        method,
        body: data,
      }),
      invalidatesTags: ["fleet", "singleFleet"],
    }),

    mutateFleetOption: builder.mutation<
      object,
      { id?: string; data?: object; method: "DELETE" | "POST" }
    >({
      query: ({ id, data, method }) => ({
        url: `/workspace/${getWorkspace()}/fleets/options/${id ? id : ""}`,
        method,
        body: data,
      }),
      invalidatesTags: ["fleetOptions"],
    }),
  }),
});

export const {
  useGetFleetQuery,
  useGetSingleFleetQuery,
  useGetFleetOptionsQuery,
  useMutateFleetMutation,
  useMutateFleetOptionMutation,
} = fleetAPi;
